import React, { useCallback, useContext, useEffect, useReducer } from 'react';
import { EditVideoStudioActionTypes } from '../editVideoStudioModalActions';
import { editVideoStudioReducer } from './editVideoStudioModalReducer';
import {
	EditVideoStudioContext,
	editVideoStudioInitialState,
	TTypeOfEditVideoStudioModalWindow,
} from './editVideoStudioModalContext';
import { IUpdateVideoStudio, IGetVideoStudioInfo } from 'types';
import { useFetchData } from 'hooks/useFetchData';
import { getVideoStudioInfo } from 'backend/videos';
import { AppContext, IAppContext } from 'appState/appContext';
import { VideoStudiosContext, IVideoStudiosContext } from '../../../../../videoStudiosState/videoStudiosContext';

const EditVideoStudioState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(editVideoStudioReducer, editVideoStudioInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const { IDForEdit } = useContext<IVideoStudiosContext>(VideoStudiosContext);

	const { response, loading, refetch } = useFetchData<IGetVideoStudioInfo, string>(
		IDForEdit ? getVideoStudioInfo : null,
		IDForEdit || '',
	);

	useEffect(() => {
		refetch();
	}, [IDForEdit]);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useEffect(() => {
		if (response) {
			dispatch({
				type: EditVideoStudioActionTypes.SET_ALL_DATA,
				payload: {
					id: response.data.videoStudio.id,
					name: response.data.videoStudio.name || '',
				},
			});
			dispatch({
				type: EditVideoStudioActionTypes.SET_INITIAL_FORM_DATA,
				payload: {
					initialFormData: {
						name: response.data.videoStudio.name || '',
					},
				},
			});
		}
	}, [response]);

	const changeName = useCallback((value: string) => {
		dispatch({
			type: EditVideoStudioActionTypes.CHANGE_NAME,
			payload: {
				name: value,
			},
		});
	}, []);

	const setDataForSubmit = useCallback((value: IUpdateVideoStudio | null) => {
		dispatch({
			type: EditVideoStudioActionTypes.SET_DATA_FOR_SUBMIT,
			payload: {
				dataForSubmit: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfEditVideoStudioModalWindow) => {
		dispatch({
			type: EditVideoStudioActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	const resetFields = useCallback(() => dispatch({ type: EditVideoStudioActionTypes.RESET_FIELDS }), []);

	return (
		<EditVideoStudioContext.Provider
			value={{
				changeName,
				setDataForSubmit,
				changeTypeOfModalWindow,
				resetFields,
				initialFormData: state.initialFormData,
				studioID: state.studioID,
				name: state.name,
				dataForSubmit: state.dataForSubmit,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</EditVideoStudioContext.Provider>
	);
};

export default React.memo(EditVideoStudioState);
